import { useState } from 'react';
import { cn } from '@/lib/utils';
import { MessageSquare, Trash2, User, ChevronDown, ChevronUp, Loader2, AlertTriangle } from 'lucide-react';
import { formatDateTime } from '@/lib/format-es';
import { useMaterialComments, useDeleteComment } from '../hooks/useStoreMaterials';

interface StoreCommentsPanelProps {
    materialId: string;
    materialTitle: string;
    commentsCount: number;
}

export function StoreCommentsPanel({ materialId, commentsCount }: StoreCommentsPanelProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
    const { data: comments, isLoading } = useMaterialComments(isExpanded ? materialId : null);
    const deleteComment = useDeleteComment();

    const handleDeleteComment = (commentId: string) => {
        setDeleteTarget(commentId);
    };

    const confirmDelete = () => {
        if (deleteTarget) {
            deleteComment.mutate(deleteTarget, {
                onSuccess: () => setDeleteTarget(null),
                onError: () => setDeleteTarget(null),
            });
        }
    };

    return (
        <div className="rounded-lg border border-border/50 overflow-hidden">
            {/* Toggle Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={cn(
                    'w-full flex items-center justify-between px-3 py-2 text-left transition-colors',
                    isExpanded ? 'bg-muted/30' : 'bg-transparent hover:bg-muted/20'
                )}
            >
                <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <MessageSquare size={12} />
                    {commentsCount} comentario{commentsCount !== 1 ? 's' : ''}
                </span>
                {isExpanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
            </button>

            {/* Comments List */}
            {isExpanded && (
                <div className="border-t border-border/30">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-6">
                            <Loader2 size={16} className="animate-spin text-muted-foreground" />
                        </div>
                    ) : !comments || comments.length === 0 ? (
                        <p className="text-xs text-muted-foreground/60 text-center py-4">
                            No hay comentarios
                        </p>
                    ) : (
                        <div className="divide-y divide-border/30 max-h-80 overflow-y-auto">
                            {comments.map(comment => (
                                <div key={comment.id} className="px-3 py-2.5 group hover:bg-muted/10 transition-colors">
                                    <div className="flex items-start gap-2">
                                        {/* Avatar */}
                                        {comment.user_avatar ? (
                                            <img
                                                src={comment.user_avatar}
                                                alt=""
                                                className="w-6 h-6 rounded-full object-cover border border-border/50 shrink-0 mt-0.5"
                                            />
                                        ) : (
                                            <div className="w-6 h-6 rounded-full bg-muted/50 flex items-center justify-center shrink-0 mt-0.5">
                                                <User size={10} className="text-muted-foreground/60" />
                                            </div>
                                        )}

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-foreground">
                                                    {comment.user_name || comment.user_email || 'Usuario'}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground/50">
                                                    {formatDateTime(comment.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-foreground/80 mt-0.5 whitespace-pre-line">
                                                {comment.text}
                                            </p>
                                            {comment.parent_comment_id && (
                                                <span className="text-[10px] text-muted-foreground/40 mt-0.5 inline-block">
                                                    ↳ Respuesta
                                                </span>
                                            )}
                                        </div>

                                        {/* Delete */}
                                        <button
                                            onClick={() => handleDeleteComment(comment.id)}
                                            className="p-1 rounded-md text-muted-foreground/30 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all shrink-0"
                                            title="Eliminar comentario"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Delete Confirmation */}
            {deleteTarget && (
                <div className="border-t border-border/30 px-3 py-2 bg-red-500/5 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs text-red-400">
                        <AlertTriangle size={12} />
                        ¿Eliminar este comentario?
                    </span>
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setDeleteTarget(null)}
                            className="px-2 py-1 rounded text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                        >
                            No
                        </button>
                        <button
                            onClick={confirmDelete}
                            disabled={deleteComment.isPending}
                            className="px-2 py-1 rounded text-[10px] font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        >
                            {deleteComment.isPending ? 'Eliminando...' : 'Sí, eliminar'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
